import { ChevronDown, ChevronLeft, ChevronRight, Mail, MessageSquare, Plus, Send, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import Head from 'expo-router/head';
import {
    ActivityIndicator,
    Linking,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    View
} from 'react-native';

import { api } from '~/api';
import { AccountShell } from '~/components/AccountShell';
import { useMember } from '~/stores/useMember';
import { colors, control, fontFamily, maxContentWidth, radii, space, spacing, type } from '~/ui/tokens';
import { FAQ_CATEGORIES } from '~/lib/faqs';
import { SkeletonBlock } from '~/components/ui';

const SUPPORT_EMAIL = 'hello@suitescanner.com';

function HelpScreen() {
    const isMember = useMember(state => state.isMember);
    const openPrompt = useMember(state => state.openPrompt);

    const [tickets, setTickets] = useState<Api.Support.Ticket[]>([]);
    const [ticketsPage, setTicketsPage] = useState(1);
    const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
    const [ticketsTotal, setTicketsTotal] = useState(0);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<Api.Support.Ticket | null>(null);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newSubject, setNewSubject] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [replyText, setReplyText] = useState('');
    const [isReplying, setIsReplying] = useState(false);

    const loadTickets = async (page: number = ticketsPage) => {
        if (!isMember) return;
        setLoadingTickets(true);
        try {
            const res = await api.support.my({ page, limit: 5 });
            if (res.data) {
                setTickets(res.data.tickets);
                setTicketsPage(res.data.page);
                setTicketsTotalPages(res.data.totalPages);
                setTicketsTotal(res.data.total);
                if (selectedTicket) {
                    const refreshed = res.data.tickets.find(t => t.ticketId === selectedTicket.ticketId);
                    if (refreshed) setSelectedTicket(refreshed);
                }
            }
        } catch {
            // error
        } finally {
            setLoadingTickets(false);
        }
    };

    useEffect(() => {
        if (isMember) loadTickets(1);
    }, [isMember]);

    const handleCreateTicket = async () => {
        if (!newSubject.trim() || !newMessage.trim()) return;
        setIsSubmitting(true);
        try {
            const res = await api.support.create({
                subject: newSubject.trim(),
                initialMessage: newMessage.trim()
            });
            if (res.data) {
                setIsCreateModalOpen(false);
                setNewSubject('');
                setNewMessage('');
                loadTickets();
                setSelectedTicket(res.data);
            }
        } catch {
            // error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendReply = async () => {
        if (!selectedTicket || !replyText.trim()) return;
        setIsReplying(true);
        try {
            const res = await api.support.message({
                ticketId: selectedTicket.ticketId,
                text: replyText.trim()
            });
            if (res.data) {
                setSelectedTicket(res.data);
                setReplyText('');
                loadTickets();
            }
        } catch {
            // error
        } finally {
            setIsReplying(false);
        }
    };

    return (
        <AccountShell active="support">
            <Head>
                <title>Help Centre — SuiteScanner</title>
                <meta
                    name="description"
                    content="Frequently asked questions about SuiteScanner pricing, cancellations, and how a direct hotel booking works end to end."
                />
            </Head>
            <View style={styles.body}>
                <Text style={styles.heading}>Help centre</Text>
                <Text style={styles.lead}>
                    Answers to the questions we hear most. Still stuck? Open a support ticket or drop us an email.
                </Text>

                {/* Support Tickets Section */}
                {isMember ? (
                    <View style={styles.ticketsBox}>
                        <View style={styles.ticketsHeader}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <MessageSquare size={18} color={colors.brand} />
                                <Text style={styles.ticketsTitle}>Your support requests</Text>
                            </View>
                            <Pressable
                                onPress={() => setIsCreateModalOpen(true)}
                                style={styles.newTicketBtn}
                            >
                                <Plus size={14} color={colors.white} />
                                <Text style={styles.newTicketBtnText}>New request</Text>
                            </Pressable>
                        </View>

                        {loadingTickets && tickets.length === 0 ? (
                            <View style={styles.ticketSkeleton} accessibilityLabel="Loading support tickets">
                                <SkeletonBlock style={styles.ticketSkeletonTitle} />
                                <SkeletonBlock style={styles.ticketSkeletonLine} />
                                <SkeletonBlock style={styles.ticketSkeletonLineShort} />
                            </View>
                        ) : tickets.length > 0 ? (
                            <>
                                <View style={styles.ticketsList}>
                                    {tickets.map(t => (
                                        <Pressable
                                            key={t.ticketId}
                                            onPress={() => setSelectedTicket(t)}
                                            style={styles.ticketCard}
                                        >
                                            <View style={styles.ticketRowHeader}>
                                                <Text style={styles.ticketIdText}>{t.ticketId}</Text>
                                                <View style={[styles.ticketPill, t.status === 'resolved' ? styles.pillResolved : styles.pillOpen]}>
                                                    <Text style={[styles.ticketPillText, t.status === 'resolved' ? styles.pillResolvedText : styles.pillOpenText]}>
                                                        {t.status.replace(/_/g, ' ')}
                                                    </Text>
                                                </View>
                                            </View>
                                            <Text style={styles.ticketSubjectText}>{t.subject}</Text>
                                            <Text style={styles.ticketMsgPreview} numberOfLines={1}>
                                                {t.messages[t.messages.length - 1]?.text ?? ''}
                                            </Text>
                                        </Pressable>
                                    ))}
                                </View>
                                {ticketsTotalPages > 1 ? (
                                    <View style={styles.helpPaginationRow}>
                                        <Text style={styles.helpPaginationInfo}>
                                            Page {ticketsPage} of {ticketsTotalPages} ({ticketsTotal} total)
                                        </Text>
                                        <View style={{ flexDirection: 'row', gap: 6 }}>
                                            <Pressable
                                                disabled={ticketsPage <= 1 || loadingTickets}
                                                onPress={() => loadTickets(ticketsPage - 1)}
                                                style={[styles.helpPageBtn, (ticketsPage <= 1 || loadingTickets) && styles.helpPageBtnDisabled]}
                                            >
                                                <ChevronLeft size={14} color={ticketsPage <= 1 ? colors.inkFaint : colors.ink} />
                                            </Pressable>
                                            <Pressable
                                                disabled={ticketsPage >= ticketsTotalPages || loadingTickets}
                                                onPress={() => loadTickets(ticketsPage + 1)}
                                                style={[styles.helpPageBtn, (ticketsPage >= ticketsTotalPages || loadingTickets) && styles.helpPageBtnDisabled]}
                                            >
                                                <ChevronRight size={14} color={ticketsPage >= ticketsTotalPages ? colors.inkFaint : colors.ink} />
                                            </Pressable>
                                        </View>
                                    </View>
                                ) : null}
                            </>
                        ) : (
                            <Text style={styles.emptyTicketsText}>You don't have any open support tickets.</Text>
                        )}
                    </View>
                ) : (
                    <View style={styles.signInPromptBox}>
                        <MessageSquare size={20} color={colors.brand} />
                        <View style={{ flex: 1 }}>
                            <Text style={styles.signInPromptTitle}>Need personal assistance?</Text>
                            <Text style={styles.signInPromptSub}>Sign in to open a support ticket regarding your stays.</Text>
                        </View>
                        <Pressable onPress={openPrompt} style={styles.signInPromptBtn}>
                            <Text style={styles.signInPromptBtnText}>Sign in</Text>
                        </Pressable>
                    </View>
                )}

                <Text style={[styles.heading, type.sectionTitle, { marginTop: space.card }]}>Common questions</Text>
                <View style={styles.faqCategories}>
                    {FAQ_CATEGORIES.map(category => (
                        <View key={category.title} style={styles.faqCategory}>
                            <Text style={styles.faqCategoryTitle}>{category.title}</Text>
                            <View style={styles.faq}>
                                {category.items.map(item => (
                                    <FaqRow key={item.question} q={item.question} a={item.answer} />
                                ))}
                            </View>
                        </View>
                    ))}
                </View>

                <View style={styles.contact}>
                    <View style={styles.contactIcon}>
                        <Mail size={20} color={colors.brand} />
                    </View>
                    <View style={styles.contactText}>
                        <Text style={styles.contactTitle}>Contact us directly</Text>
                        <Text style={styles.contactSub}>
                            Email{' '}
                            <Text
                                style={styles.link}
                                accessibilityRole="link"
                                onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}
                            >
                                {SUPPORT_EMAIL}
                            </Text>{' '}
                            and our team will get back to you.
                        </Text>
                    </View>
                </View>
            </View>

            {/* Ticket Conversation Modal */}
            {selectedTicket ? (
                <Modal visible transparent animationType="fade">
                    <View style={styles.modalOverlay}>
                        <View style={styles.chatModalCard}>
                            <View style={styles.chatModalHeader}>
                                <View style={{ flex: 1 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Text style={styles.chatModalTitle}>{selectedTicket.ticketId}</Text>
                                        <View style={[styles.ticketPill, selectedTicket.status === 'resolved' ? styles.pillResolved : styles.pillOpen]}>
                                            <Text style={[styles.ticketPillText, selectedTicket.status === 'resolved' ? styles.pillResolvedText : styles.pillOpenText]}>
                                                {selectedTicket.status.replace(/_/g, ' ')}
                                            </Text>
                                        </View>
                                    </View>
                                    <Text style={styles.chatModalSub}>{selectedTicket.subject}</Text>
                                </View>
                                <Pressable onPress={() => setSelectedTicket(null)}>
                                    <X size={20} color={colors.ink} />
                                </Pressable>
                            </View>

                            <ScrollView style={styles.chatMessagesScroll} contentContainerStyle={styles.chatMessagesContent}>
                                {selectedTicket.messages.map((m, idx) => {
                                    const isUserMsg = m.senderRole === 'user';
                                    return (
                                        <View
                                            key={idx}
                                            style={[
                                                styles.chatBubble,
                                                isUserMsg ? styles.chatBubbleUser : styles.chatBubbleAdmin
                                            ]}
                                        >
                                            <Text style={styles.chatSender}>
                                                {m.senderName} {m.senderRole === 'admin' ? '(SuiteScanner Support)' : ''}
                                            </Text>
                                            <Text style={[styles.chatText, !isUserMsg && styles.chatTextAdmin]}>
                                                {m.text}
                                            </Text>
                                        </View>
                                    );
                                })}
                            </ScrollView>

                            <View style={styles.chatReplyBox}>
                                <TextInput
                                    value={replyText}
                                    onChangeText={setReplyText}
                                    placeholder="Type your message…"
                                    placeholderTextColor={colors.inkFaint}
                                    style={styles.chatInput}
                                />
                                <Pressable
                                    onPress={handleSendReply}
                                    disabled={isReplying || !replyText.trim()}
                                    style={[styles.chatSendBtn, (!replyText.trim() || isReplying) && styles.chatSendDisabled]}
                                >
                                    {isReplying ? (
                                        <ActivityIndicator size="small" color={colors.white} />
                                    ) : (
                                        <Send size={15} color={colors.white} />
                                    )}
                                </Pressable>
                            </View>
                        </View>
                    </View>
                </Modal>
            ) : null}

            {/* Create Ticket Modal */}
            {isCreateModalOpen ? (
                <Modal visible transparent animationType="fade">
                    <View style={styles.modalOverlay}>
                        <View style={styles.createModalCard}>
                            <View style={styles.chatModalHeader}>
                                <Text style={styles.chatModalTitle}>Open a Support Ticket</Text>
                                <Pressable onPress={() => setIsCreateModalOpen(false)}>
                                    <X size={20} color={colors.ink} />
                                </Pressable>
                            </View>

                            <View style={styles.modalForm}>
                                <Text style={styles.fieldLabel}>Subject</Text>
                                <TextInput
                                    value={newSubject}
                                    onChangeText={setNewSubject}
                                    placeholder="Brief summary of your question"
                                    style={styles.formInput}
                                />

                                <Text style={styles.fieldLabel}>Message</Text>
                                <TextInput
                                    value={newMessage}
                                    onChangeText={setNewMessage}
                                    placeholder="Explain how we can help you…"
                                    multiline
                                    style={[styles.formInput, { height: 100, textAlignVertical: 'top' }]}
                                />
                            </View>

                            <View style={styles.createModalFooter}>
                                <Pressable onPress={() => setIsCreateModalOpen(false)} style={styles.cancelModalBtn}>
                                    <Text style={styles.cancelModalText}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={handleCreateTicket}
                                    disabled={isSubmitting || !newSubject.trim() || !newMessage.trim()}
                                    style={[
                                        styles.submitModalBtn,
                                        (!newSubject.trim() || !newMessage.trim() || isSubmitting) && styles.submitDisabled
                                    ]}
                                >
                                    {isSubmitting ? (
                                        <ActivityIndicator size="small" color={colors.white} />
                                    ) : (
                                        <Text style={styles.submitModalText}>Submit</Text>
                                    )}
                                </Pressable>
                            </View>
                        </View>
                    </View>
                </Modal>
            ) : null}

        </AccountShell>
    );
}

function FaqRow({ q, a }: { q: string; a: string }) {
    const [open, setOpen] = useState(false);

    return (
        <Pressable accessibilityRole="button" onPress={() => setOpen(value => !value)} style={styles.faqRow}>
            <View style={styles.faqQRow}>
                <Text style={styles.faqQ}>{q}</Text>
                <ChevronDown size={18} color={colors.inkFaint} />
            </View>
            {open ? <Text style={styles.faqA}>{a}</Text> : null}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    screen: { flex: 1, backgroundColor: colors.cream },
    content: { flexGrow: 1 },
    body: { maxWidth: 720, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor, gap: space.related, flexGrow: 1 },
    heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink },
    lead: { fontFamily: fontFamily.regular, ...type.bodyLarge, color: colors.inkSoft, maxWidth: 520 },
    ticketsBox: { backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, padding: space.card, gap: space.compact, marginTop: space.card },
    ticketsHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    ticketsTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
    newTicketBtn: { flexDirection: 'row', alignItems: 'center', gap: space.micro, backgroundColor: colors.brand, paddingVertical: spacing(0.75), paddingHorizontal: spacing(1.5), borderRadius: radii.md },
    newTicketBtnText: { fontFamily: fontFamily.semibold, ...control.utilityCta, color: colors.white },
    ticketsList: { gap: space.related },
    ticketSkeleton: { gap: space.related, paddingVertical: space.compact },
    ticketSkeletonTitle: { width: '42%', height: 18 },
    ticketSkeletonLine: { width: '86%', height: 12 },
    ticketSkeletonLineShort: { width: '64%', height: 12 },
    ticketCard: { padding: space.internal, borderRadius: radii.md, backgroundColor: colors.cream, borderWidth: 1, borderColor: colors.lineSoft, gap: space.micro },
    ticketRowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    ticketIdText: { fontFamily: fontFamily.bold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
    ticketPill: { paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.625), borderRadius: radii.full },
    pillOpen: { backgroundColor: colors.skyTint },
    pillResolved: { backgroundColor: colors.greenTint },
    ticketPillText: { fontFamily: fontFamily.semibold, ...control.badgeCompact, textTransform: 'capitalize' },
    pillOpenText: { color: colors.brand },
    pillResolvedText: { color: colors.green },
    ticketSubjectText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
    ticketMsgPreview: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    emptyTicketsText: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint, paddingVertical: space.related },
    signInPromptBox: { flexDirection: 'row', alignItems: 'center', gap: space.compact, backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: space.internal, marginTop: space.card },
    signInPromptTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
    signInPromptSub: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    signInPromptBtn: { backgroundColor: colors.brand, paddingVertical: spacing(0.75), paddingHorizontal: spacing(1.5), borderRadius: radii.md },
    signInPromptBtnText: { fontFamily: fontFamily.semibold, ...control.utilityCta, color: colors.white },
    faqCategories: { gap: space.card, marginTop: space.card },
    faqCategory: { gap: space.compact },
    faqCategoryTitle: { fontFamily: fontFamily.semibold, ...type.cardTitle, color: colors.ink },
    faq: { gap: space.related },
    faqRow: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.card, padding: space.internal },
    faqQRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.internal },
    faqQ: { flexGrow: 1, flexBasis: 0, minWidth: 0, fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
    faqA: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft, marginTop: space.related },
    contact: { flexDirection: 'row', gap: space.internal, alignItems: 'flex-start', backgroundColor: colors.skyTint, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: space.card, marginTop: space.card },
    contactIcon: { width: 44, height: 44, borderRadius: radii.full, backgroundColor: colors.white, alignItems: 'center', justifyContent: 'center' },
    contactText: { flexGrow: 1, flexBasis: 0, minWidth: 0, gap: space.related },
    contactTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
    contactSub: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    link: { fontFamily: fontFamily.semibold, color: colors.brand },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: space.internal },
    chatModalCard: { width: '100%', maxWidth: 540, backgroundColor: colors.card, borderRadius: radii.lg, padding: space.card, maxHeight: '85%', display: 'flex' },
    chatModalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.compact, paddingBottom: space.related, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
    chatModalTitle: { fontFamily: fontFamily.bold, fontSize: type.cardTitle.fontSize, color: colors.ink },
    chatModalSub: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft, marginTop: space.micro },
    chatMessagesScroll: { flex: 1, marginVertical: space.related },
    chatMessagesContent: { gap: space.compact, paddingBottom: space.related },
    chatBubble: { maxWidth: '80%', padding: space.compact, borderRadius: radii.md },
    chatBubbleUser: { alignSelf: 'flex-end', backgroundColor: colors.skyTint },
    chatBubbleAdmin: { alignSelf: 'flex-start', backgroundColor: colors.lineSoft },
    chatSender: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.inkFaint, marginBottom: space.micro },
    chatText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink },
    chatTextAdmin: { color: colors.ink },
    chatReplyBox: { flexDirection: 'row', gap: space.related, paddingTop: space.related, borderTopWidth: 1, borderTopColor: colors.lineSoft, alignItems: 'center' },
    chatInput: { flex: 1, backgroundColor: colors.cream, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: space.compact, paddingVertical: space.related, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink },
    chatSendBtn: { backgroundColor: colors.brand, padding: space.compact, borderRadius: radii.md, alignItems: 'center', justifyContent: 'center' },
    chatSendDisabled: { opacity: 0.5 },
    createModalCard: { width: '100%', maxWidth: 480, backgroundColor: colors.card, borderRadius: radii.lg, padding: space.card },
    modalForm: { gap: space.internal },
    fieldLabel: { fontFamily: fontFamily.semibold, ...control.inputLabel, color: colors.ink, marginBottom: space.related },
    formInput: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: space.compact, paddingVertical: space.compact, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink, backgroundColor: colors.cream },
    createModalFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: space.compact, marginTop: space.component },
    cancelModalBtn: { borderWidth: 1, borderColor: colors.line, paddingVertical: space.related, paddingHorizontal: space.internal, borderRadius: radii.md },
    cancelModalText: { fontFamily: fontFamily.medium, ...control.secondaryCta, color: colors.inkSoft },
    submitModalBtn: { backgroundColor: colors.brand, paddingVertical: space.related, paddingHorizontal: space.component, borderRadius: radii.md, alignItems: 'center', justifyContent: 'center' },
    submitDisabled: { opacity: 0.5 },
    submitModalText: { fontFamily: fontFamily.semibold, ...control.secondaryCta, color: colors.white },
    helpPaginationRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: space.compact, borderTopWidth: 1, borderTopColor: colors.lineSoft },
    helpPaginationInfo: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    helpPageBtn: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.625), backgroundColor: colors.card },
    helpPageBtnDisabled: { opacity: 0.4 }
});

export default HelpScreen;
